import { merge } from 'lodash'
import { applySnapshot, Instance, SnapshotOut, types } from 'mobx-state-tree'

import logger from '../../logger'
import { withEventEmitter } from '../EventEmitter'

import { defaultConfig } from './config-main'
import { Entries, RemoteConfigStore, RemoteConfigStoreSnapshot } from './RemoteConfigStore'

const defaultRemoteConfigUrl =
  'https://businesses.api.vatominc.com/viewers/config?hostname=wallet.vatom.com'

interface VatomFilterField {
  field?: string
  value?: string
}

interface RemoteConfig {
  checkInterval: string
  updateMode: string
  url: string
}

interface VolatileConfig {
  filter: VatomFilterField
  remoteConfig: RemoteConfig
  _remoteConfigTimer?: NodeJS.Timer
}

/** Get a vatom field based on a string path */
const vatomField = (vatom: any, path: string) => {
  // Split path into components
  const components = path.split(/\.(?=(?:[^"]*"[^"]*")*[^"]*$)/g)
  let current = vatom
  for (const nextComponent of components) {
    if (typeof current[nextComponent] !== 'undefined') current = current[nextComponent]
  }

  // Got value
  return current
}

// export function jsonMerge(target: any, source: any) {
//   function isObject(obj: any) {
//     if (typeof obj === 'object') {
//       for (const key in obj) {
//         if (obj.hasOwnProperty(key)) {
//           return true // search for first object prop
//         }
//       }
//     }
//     return false
//   }
//   for (const key in source) {
//     if (source.hasOwnProperty(key)) {
//       if (target[key] && isObject(target[key]) && isObject(source[key])) {
//         jsonMerge(target[key], source[key])
//       } else {
//         target[key] = source[key]
//       }
//     }
//   }
//   return target
// }
export const jsonMerge = (target: any, source: any) => {
  return merge(target, source)
}

export const ConfigApiStore = types.model('ConfigApiStore').props({
  // api
  vatom: types.optional(types.string, 'https://api.vi.vatom.network'),
  vatoms: types.optional(types.string, 'https://vatoms.api.vatominc.com'),
  userVatom: types.optional(types.string, 'https://api.vi.vatom.network'),
  oidc: types.optional(types.string, 'https://id.vatom.com'),
  geo: types.optional(types.string, 'https://maps.googleapis.com'),
  billing: types.optional(types.string, 'https://billing.api.vatominc.com'),
  businesses: types.optional(types.string, 'https://businesses.api.vatominc.com'),
  studio: types.optional(types.string, 'https://studio.api.vatominc.com'),
  points: types.optional(types.string, 'https://points.api.vatominc.com'),
  events: types.optional(types.string, 'https://events.api.vatominc.com'),
  loyalty: types.optional(types.string, 'https://loyalty.api.vatominc.com')
})

export const ConfigAuthenticationStore = types.model('ConfigAuthenticationStore').props({
  // authentication
  clientId: types.optional(types.string, ''),
  useProxy: types.optional(types.boolean, false),
  discoveryUrl: types.optional(types.string, ''),
  redirectUri: types.optional(types.string, ''),
  scopes: types.optional(types.array(types.string), [])
})

// Firebase configuration options
export const ConfigFirebaseStore = types.model('ConfigFirebaseStore').props({
  apiKey: types.optional(types.string, ''),
  authDomain: types.optional(types.string, ''),
  databaseURL: types.optional(types.string, ''),
  projectId: types.optional(types.string, ''),
  storageBucket: types.optional(types.string, ''),
  messagingSenderId: types.optional(types.string, ''),
  appId: types.optional(types.string, '')
})

const ConfigMapPickup = types.model('ConfigMapPickup').props({
  enabled: types.optional(types.boolean, true),
  redirect: types.optional(types.string, 'ar')
})

const ConfigMap = types.model('ConfigMap').props({
  pickup: ConfigMapPickup,
  fqdn_whitelist: types.optional(types.string, ''),
  max_items: types.optional(types.number, 20)
})

export const ConfigFeatures = types.model('ConfigFeatures').props({
  maps: ConfigMap
})

export const ConfigPropsStore = types.model('ConfigPropsStore').props({
  api: types.optional(ConfigApiStore, {}),
  authentication: types.optional(ConfigAuthenticationStore, {}),
  //
  //  Project: Generic Web Viewer
  //

  server: types.optional(types.string, 'https://api.vi.vatom.network'),
  websocketServer: types.optional(types.string, 'wss://ws.vatominc.net'),
  // This should be removed and replaced with a discovery via authentication prop above
  viewerId: types.optional(types.string, '94JHkdj8jF83jfFF2LI8Q4'),
  studioServer: types.optional(types.string, 'https://studio.vatom.com'),

  // Your developer key. You can get one from developer.blockv.io
  appID: types.optional(types.string, ''),
  // MapBox API Key. See https://docs.mapbox.com/mapbox-gl-js/overview/#quickstart to obtain a key
  mapBoxKey: types.optional(types.string, ''),
  // Google Maps Key
  googleMapsKey: types.optional(types.string, ''),
  // googleContactsClientID: '3679106328-shu3ek38ruqhii8nqusbjsj77b4on7d5.apps.googleusercontent.com',
  googleContactsClientID: types.optional(types.string, ''),
  // Google Analytics Tracking ID
  analyticsTrackingID: types.optional(types.string, ''),
  // Catchoom Token for image recognition. See https://catchoom.com/image-recognition/ to obtain a token
  catchoomToken: types.optional(types.string, ''),
  // defaultCampaignId: 'DJVCwpz2hK',
  geoLocationApi: types.optional(types.string, ''),
  algoliaApiKey: types.optional(types.string, ''),
  algoliaAppId: types.optional(types.string, ''),
  // Default root for the web app router
  baseName: types.optional(types.string, '/'),
  // Firebase configuration options
  firebase: types.optional(ConfigFirebaseStore, {}),
  // Firebase messaging VAPID key. See https://firebase.google.com/docs/cloud-messaging/js/client#generate_a_new_key_pair
  firebaseVapidKey: types.optional(types.string, ''),
  // Optional 8th wall app key, provides 6DoF movement in AR
  appKey8thWall: types.optional(types.string, ''),
  // Disable v2 sync with vatom inventory
  disableSyncV2: false,

  moralisServerUrl: types.optional(types.string, 'https://parser.api.vatominc.com/server'),

  moralisAppId: types.optional(types.string, 'KPwA7E3j_p5Pvme1XRhkF'),

  features: types.maybe(ConfigFeatures),
  tosLink: types.optional(
    types.string,
    'https://resources.vatom.com/system/terms/10172022/terms.pdf'
  ),
  privacyLink: types.optional(
    types.string,
    'https://resources.vatom.com/system/privacy/04212022/privacy-policy.pdf'
  )
})
export type ConfigPropsStoreType = Instance<typeof ConfigStore>
export type ConfigPropsStoreSnapshot = SnapshotOut<typeof ConfigStore>

export const ConfigStore = types
  .compose(
    ConfigPropsStore,
    RemoteConfigStore,
    types.model('ConfigPlaceholderStore').props({
      _remoteConfigURL: types.optional(types.string, ''),
      _remoteConfigString: types.optional(types.string, ''),
      // _remoteConfigTimer: NodeJS.Timeout | undefined
      prefix: types.optional(types.string, ''),
      hasPendingUpdate: types.optional(types.boolean, false)
    })
  )
  .actions(self => ({
    setRemoteConfigUrl(configUrl: string) {
      self._remoteConfigURL = configUrl
    },
    setRemoteConfigString(configString: string) {
      self._remoteConfigString = configString
    },
    setRemoteConfigTimer(timer: NodeJS.Timer) {
      // @ts-ignore
      self._remoteConfigTimer = timer
    },
    setPendingUpdate(hasPendingUpdate: boolean) {
      self.hasPendingUpdate = hasPendingUpdate
    }
  }))
  .volatile(_self => {
    const filter: VatomFilterField = { field: undefined, value: undefined }
    const remoteConfig = {
      checkInterval: '60000',
      updateMode: '',
      url: defaultRemoteConfigUrl
    }
    return {
      filter,
      remoteConfig,
      _remoteConfigTimer: undefined
    } as VolatileConfig
  })
  .extend(withEventEmitter)
  .actions(self => ({
    /** Load remote config from cached copy */
    loadRemoteConfigFromCache() {
      // // Catch errors
      // try {
      //   // Parse config
      //   const str = localStorage['config.cache.' + self.prefix]
      //   // Stop if there is no remote config. Due to multi-tenant config, this will result in the default config being applied
      //   if (!str) return
      //   const json = JSON.parse(str)
      //   merge(this, json)
      //   // Store latest config string
      //   self._remoteConfigString = str
      // } catch (err) {
      //   // Throw error
      //   if (err instanceof Error)
      //     throw new Error(
      //       'Unable to load config from local cache. ' + err.message
      //     )
      //   else throw err
      // }
    },

    /** Checks for config updates on a schedules interval */
    async checkRemoteConfigForUpdates() {
      // Catch errors
      try {
        // Fetch it
        const str = await fetch(self._remoteConfigURL).then(r => r.text())
        // Check for change, stop if none
        if (str === self._remoteConfigString || !str) return

        // Store new config locally
        self._remoteConfigString = str
        localStorage['config.cache.' + self.prefix] = str
        // New config available!
        if (self.remoteConfig?.updateMode === 'reload') {
          // Force reload the page
          logger.info(`[Config] New config found, reloading the page...`)

          // window.location.reload()
        } else if (self.remoteConfig?.updateMode === 'force') {
          // Force install the config into the existing app
          const json = JSON.parse(str)
          logger.info(`[Config] New config found, installing into existing instance...`)
          merge(this, json)
          self.emit('updated')
        } else {
          // Just notify the user of an update
          logger.info(`[Config] New config found, showing notification to user...`)
          self.setPendingUpdate(true)
          self.emit('updated')
        }
      } catch (err) {
        // Throw error
        if (err instanceof Error) {
          logger.warn('[Config] Unable to check for update. ' + err.message)
        }
      }
    },

    setRemoteConfig(json: any) {
      const keys = Object.keys(self)
      Object.entries<Entries<RemoteConfigStoreSnapshot>>(json).forEach(([key, value], index) => {
        if (keys.includes(key)) {
          const k = key as keyof RemoteConfigStoreSnapshot
          self[k] = value
        }
      })
    }
  }))

  .actions(self => ({
    /** Call to load the remote config. @returns Promise. */
    async loadRemoteConfig() {
      // Get config URL, replace variables
      const configURL = self.remoteConfig?.url || defaultRemoteConfigUrl
      // @TODO: setup $HOSTNAME env var
      // configURL = configURL.replace(/\$HOSTNAME/g, window.location.hostname)
      // Stop if there is no remote config
      if (!configURL) return
      // Start update timer if necessary
      const checkInterval = parseInt(self.remoteConfig?.checkInterval, 10)
      if (checkInterval) {
        self.setRemoteConfigUrl(configURL)
        self.setRemoteConfigString('')
        self.setRemoteConfigTimer(
          setInterval(() => self.checkRemoteConfigForUpdates(), checkInterval)
        )
      }
      // Fetch config
      try {
        // Fetch it
        const resp = await fetch(configURL)
        if (resp.status === 404) {
          return
        }
        const json = await resp.json()
        // Merge it in
        self.setRemoteConfig(json) // merge(this, json)

        const str = JSON.stringify(json)
        // Store in local cache
        // localStorage['config.cache.' + self.prefix] = str
        // Store latest config string
        self.setRemoteConfigString(str)
      } catch (err) {
        if (err instanceof Error)
          logger.warn('Failed to load remote config, using cached copy... ' + err.message)
        // Attempt to load config from local cache instead
        // self.loadRemoteConfigFromCache()
      }
    },

    /** Returns true if the specified vatom is allowed to be shown based on the config's filter */
    canShowToken(token: any) {
      // Check if a filter exists
      if (!self.filter?.field) return true

      // Compare values
      const vatomValue = vatomField(token.payload, self.filter?.field)
      return vatomValue === self.filter?.value
    }
  }))
  .actions(self => {
    const afterCreate = () => {
      // logger.debug('ConfigStore.afterCreate defaultConfig')
      // Merge fields from local config
      applySnapshot(self, defaultConfig)
      // logger.debug('ConfigStore.afterCreate defaultConfig', getSnapshot(self))
      self.setRemoteConfigUrl('')
      self.setRemoteConfigString('')
      self.hasPendingUpdate = false
      self.prefix = ''
    }
    const beforeDestroy = () => {
      // teardown
    }
    return { afterCreate, beforeDestroy }
  })

export type ConfigStoreType = Instance<typeof ConfigStore>
export type Config = ConfigStoreType
export type ConfigStoreSnapshot = SnapshotOut<typeof ConfigStore>
